import { FiatDepositStepNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-names.enum";
import { FiatDepositParamNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-param-names.enum";
import { GenericCollection } from "@modules/+new/payments/shared-payments/entities/generic-collection.model";

type StepValidationParamsFunction = (fromBack?: boolean) => FiatDepositParamNames[];

const getSelectDepositTypeValidationParams = (): FiatDepositParamNames[] => [];

const getSelectFiatWalletValidationParams = (): FiatDepositParamNames[] => [];

const getSelectCryptoWalletValidationParams = (): FiatDepositParamNames[] => [];

const getSelectPaymentOptionValidationParams = (fromBack: boolean): FiatDepositParamNames[] => {
	const paramsToValidate = [];
	paramsToValidate.push(FiatDepositParamNames.Wallet, FiatDepositParamNames.FiatPid);

	if (fromBack) {
		paramsToValidate.push(FiatDepositParamNames.PaymentOption);
	}
	return paramsToValidate;
};

const getEnterAmountValidationParams = (fromBack: boolean, fromCheckout: boolean): FiatDepositParamNames[] => {
	const paramsToValidate = [];
	paramsToValidate.push(
		FiatDepositParamNames.Wallet,
		FiatDepositParamNames.FiatPid,
		FiatDepositParamNames.PaymentOption
	);

	if (fromBack || fromCheckout) {
		paramsToValidate.push(FiatDepositParamNames.DepositAmount, FiatDepositParamNames.IncludingFees);
	}

	return paramsToValidate;
};

const getSummaryValidationParams = (): FiatDepositParamNames[] => [
	FiatDepositParamNames.Wallet,
	FiatDepositParamNames.FiatPid,
	FiatDepositParamNames.PaymentOption,
	FiatDepositParamNames.DepositAmount,
	FiatDepositParamNames.IncludingFees
];

const getDepositVerifyPinValidationParams = (): FiatDepositParamNames[] => [
	...getSummaryValidationParams(),
	FiatDepositParamNames.Transaction
];

const getDepositPendingValidationParams = (): FiatDepositParamNames[] => [FiatDepositParamNames.PaymentUrl];

const getManualDepositReminderValidationParams = (): FiatDepositParamNames[] => getSummaryValidationParams();
const getManualDepositInstructionsValidationParams = (): FiatDepositParamNames[] => getSummaryValidationParams();

const getSelectBankValidationParams = (): FiatDepositParamNames[] => [
	...getSummaryValidationParams(),
	FiatDepositParamNames.BankProvider
];

const getSelectBankChildValidationParams = (): FiatDepositParamNames[] => [
	...getSelectBankValidationParams(),
	FiatDepositParamNames.ParentBank
];

const getConnectToBankValidationParams = (): FiatDepositParamNames[] => [
	...getSelectBankChildValidationParams(),
	FiatDepositParamNames.Bank
];

const getSelectNetworkValidationParams = (): FiatDepositParamNames[] => [FiatDepositParamNames.AssetSymbol];

const getCryptoDepositWarningValidationParams = (): FiatDepositParamNames[] => [
	...getSelectNetworkValidationParams(),
	FiatDepositParamNames.NetworkId,
	FiatDepositParamNames.Wallet
];

const getCryptoDepositSummaryValidationParams = (): FiatDepositParamNames[] => [
	FiatDepositParamNames.AssetSymbol,
	FiatDepositParamNames.NetworkId
];

const getCryptoDepositWalletDetailsValidationParams = (): FiatDepositParamNames[] => [
	FiatDepositParamNames.AssetSymbol,
	FiatDepositParamNames.NetworkId,
	FiatDepositParamNames.Wallet
];

const getCryptoDepositNonDefaultAddressValidationParams = (): FiatDepositParamNames[] => [
	FiatDepositParamNames.AssetSymbol,
	FiatDepositParamNames.NetworkId
];

const getAllValidationParams = (): FiatDepositParamNames[] => getConnectToBankValidationParams();

const getDepositValidationParams = (
	fromBack: boolean,
	fromCheckout: boolean
): GenericCollection<StepValidationParamsFunction> => ({
	[FiatDepositStepNames.SelectDepositType]: getSelectDepositTypeValidationParams,
	[FiatDepositStepNames.SelectFiatWallet]: getSelectFiatWalletValidationParams,
	[FiatDepositStepNames.SelectCryptoWallet]: getSelectCryptoWalletValidationParams,
	[FiatDepositStepNames.SelectPaymentOption]: () => getSelectPaymentOptionValidationParams(fromBack),
	[FiatDepositStepNames.EnterAmount]: () => getEnterAmountValidationParams(fromBack, fromCheckout),
	[FiatDepositStepNames.Summary]: getSummaryValidationParams,
	[FiatDepositStepNames.DepositVerifyPin]: getDepositVerifyPinValidationParams,
	[FiatDepositStepNames.DepositPending]: getDepositPendingValidationParams,
	[FiatDepositStepNames.ManualDepositReminder]: getManualDepositReminderValidationParams,
	[FiatDepositStepNames.ManualDepositInstructions]: getManualDepositInstructionsValidationParams,
	[FiatDepositStepNames.SelectBank]: getSelectBankValidationParams,
	[FiatDepositStepNames.SelectBankChild]: getSelectBankChildValidationParams,
	[FiatDepositStepNames.ConnectToBank]: getConnectToBankValidationParams,
	[FiatDepositStepNames.SelectNetwork]: getSelectNetworkValidationParams,
	[FiatDepositStepNames.CryptoDepositSummary]: getCryptoDepositSummaryValidationParams,
	[FiatDepositStepNames.CryptoDepositWarning]: getCryptoDepositWarningValidationParams,
	[FiatDepositStepNames.DepositCryptoWallet]: getCryptoDepositWalletDetailsValidationParams,
	[FiatDepositStepNames.DepositNonDefaultAddress]: getCryptoDepositNonDefaultAddressValidationParams,
	default: getAllValidationParams
});

export const prepareParamsToValidate = (
	stepName: FiatDepositStepNames,
	fromBack: boolean,
	fromCheckout: boolean
): FiatDepositParamNames[] =>
	(
		getDepositValidationParams(fromBack, fromCheckout)[stepName] ||
		getDepositValidationParams(fromBack, fromCheckout)["default"]
	)();
